import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown, ArrowUp, Check, FileText, FolderDown,
  Gavel, Pencil, Plus, ScrollText, Search, X,
} from 'lucide-react';
import api from '../../lib/api';
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  Input,
  Select,
  Skeleton,
  SkeletonTable,
  Tag,
  type TagTone,
} from '../ui';
import { PartyBadge, type SourceParty } from '../AccountCard';
import { formatTxnDate } from '../../utils/dates';

// Trial Pack: pick originals from the document registry, get numbered
// exhibits with an authentication path each (source_party → TRCP 193.7 /
// sponsoring witness / TRE 902(10)), download the paperwork. The design goal
// is zero thinking: add documents on the left, the right side explains how
// each one gets into evidence, three buttons produce the court-ready files.

interface ExhibitDocument {
  id: number;
  filename: string;
  content_type: string | null;
  doc_type: string | null;
  source_party: SourceParty;
  pdf_hash: string | null;
  uploaded_at: string | null;
  has_original: boolean;
  statements: {
    id: number;
    bank_label: string | null;
    statement_start_date: string | null;
    statement_end_date: string | null;
  }[];
}

interface Admissibility {
  path: 'self_authenticated' | 'sponsoring_witness' | 'business_records' | 'unresolved';
  rule: string | null;
  label: string;
  explanation: string;
  action: string;
}

interface ExhibitRow {
  id: number;
  position: number;
  number_label: string;
  title: string;
  sponsoring_witness: string | null;
  document: ExhibitDocument;
  admissibility: Admissibility;
}

interface ExhibitSetPayload {
  id: number;
  name: string;
  created_at: string | null;
  exhibits: ExhibitRow[];
  readiness: { ready: number; total: number; unresolved: number };
  notice_needed: boolean;
  opposing_exhibit_count: number;
}

interface CaseDocument {
  id: number;
  filename: string;
  doc_type: string | null;
  source_party: SourceParty;
  uploaded_at: string | null;
  has_original: boolean;
  statements: { id: number; bank_label: string | null; statement_start_date: string | null; statement_end_date: string | null }[];
}

const ADMISSIBILITY_TONE: Record<Admissibility['path'], TagTone> = {
  self_authenticated: 'good',
  sponsoring_witness: 'info',
  business_records: 'warn',
  unresolved: 'danger',
};

function docSubtitle(doc: CaseDocument | ExhibitDocument): string {
  const stmts = doc.statements ?? [];
  if (stmts.length > 0) {
    const accounts = [...new Set(stmts.map((s) => s.bank_label).filter(Boolean))];
    const starts = stmts.map((s) => s.statement_start_date).filter(Boolean).sort();
    const ends = stmts.map((s) => s.statement_end_date).filter(Boolean).sort();
    const period = starts.length && ends.length
      ? ` · ${formatTxnDate(starts[0]!)} – ${formatTxnDate(ends[ends.length - 1]!)}`
      : '';
    return `${accounts.join(', ')}${period}`;
  }
  return doc.doc_type ? doc.doc_type.replace(/_/g, ' ') : 'document';
}

async function downloadBlob(url: string, filename: string) {
  const res = await api.get(url, { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

interface Props {
  caseId: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
}

export default function TrialPackTab({ caseId, clientName, opposingPartyName }: Props) {
  const queryClient = useQueryClient();
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [newSetName, setNewSetName] = useState('');
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  const setsQuery = useQuery({
    queryKey: ['exhibit-sets', caseId],
    queryFn: async () => (await api.get(`/cases/${caseId}/exhibit-sets`)).data.sets as ExhibitSetPayload[],
  });
  const docsQuery = useQuery({
    queryKey: ['documents', caseId],
    queryFn: async () => (await api.get(`/cases/${caseId}/documents`)).data.documents as CaseDocument[],
  });

  const sets = setsQuery.data ?? [];
  const activeSet = sets.find((s) => s.id === selectedSetId) ?? sets[0] ?? null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['exhibit-sets', caseId] });

  const createSet = useMutation({
    mutationFn: async (name: string) =>
      (await api.post(`/cases/${caseId}/exhibit-sets`, { name })).data as ExhibitSetPayload,
    onSuccess: (created) => {
      setSelectedSetId(created.id);
      setNewSetName('');
      setCreating(false);
      invalidate();
    },
  });
  const addDocs = useMutation({
    mutationFn: async (documentIds: number[]) =>
      api.post(`/exhibit-sets/${activeSet!.id}/exhibits`, { document_ids: documentIds }),
    onSuccess: invalidate,
  });
  const removeExhibit = useMutation({
    mutationFn: async (exhibitId: number) =>
      api.delete(`/exhibit-sets/${activeSet!.id}/exhibits/${exhibitId}`),
    onSuccess: invalidate,
  });
  const reorder = useMutation({
    mutationFn: async (exhibitIds: number[]) =>
      api.post(`/exhibit-sets/${activeSet!.id}/reorder`, { exhibit_ids: exhibitIds }),
    onSuccess: invalidate,
  });
  const patchExhibit = useMutation({
    mutationFn: async ({ id, ...body }: { id: number; title?: string; sponsoring_witness?: string }) =>
      api.patch(`/exhibit-sets/${activeSet!.id}/exhibits/${id}`, body),
    onSuccess: invalidate,
  });

  const inSetDocIds = useMemo(
    () => new Map((activeSet?.exhibits ?? []).map((ex) => [ex.document.id, ex.number_label])),
    [activeSet],
  );

  const filteredDocs = useMemo(() => {
    const docs = docsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((d) =>
      `${d.filename} ${docSubtitle(d)}`.toLowerCase().includes(q),
    );
  }, [docsQuery.data, search]);

  const move = (index: number, delta: number) => {
    if (!activeSet) return;
    const ids = activeSet.exhibits.map((e) => e.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids);
  };

  const download = async (kind: 'zip' | 'list' | 'notice') => {
    if (!activeSet) return;
    setDownloading(kind);
    try {
      if (kind === 'zip') {
        await downloadBlob(`/exhibit-sets/${activeSet.id}/trial-pack.zip`, `Trial Pack — ${activeSet.name}.zip`);
      } else if (kind === 'list') {
        await downloadBlob(`/exhibit-sets/${activeSet.id}/exhibit-list.docx`, `Exhibit List — ${activeSet.name}.docx`);
      } else {
        await downloadBlob(`/exhibit-sets/${activeSet.id}/notice.docx`, `Notice of Intent (TRCP 193.7) — ${activeSet.name}.docx`);
      }
    } finally {
      setDownloading(null);
    }
  };

  if (setsQuery.isLoading || docsQuery.isLoading) {
    return (
      <div className="max-w-6xl space-y-5">
        <div className="flex items-center justify-between gap-4">
          <Skeleton variant="rect" width={240} height={32} />
          <Skeleton variant="rect" width={320} height={32} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,5fr)_minmax(0,7fr)] gap-6 items-start">
          <SkeletonTable rows={8} columns={2} />
          <SkeletonTable rows={5} columns={3} />
        </div>
      </div>
    );
  }

  // ── First-run: name the set, one field, one button ─────────────────────────
  if (sets.length === 0) {
    return (
      <EmptyState
        icon={Gavel}
        title="Build a trial pack"
        description="Pick documents, get numbered exhibits — each with how it gets into evidence — and download the exhibit list, the 193.7 notice, and every original in one ZIP."
        className="max-w-lg mx-auto mt-16"
        action={
          <form
            className="flex w-96 max-w-full gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (newSetName.trim()) createSet.mutate(newSetName.trim());
            }}
          >
            <div className="flex-1">
              <Input
                autoFocus
                value={newSetName}
                onChange={(e) => setNewSetName(e.target.value)}
                placeholder={'Name this set — e.g. "Final Trial" or "Temp Orders 8/12"'}
              />
            </div>
            <Button type="submit" size="lg" loading={createSet.isPending} disabled={!newSetName.trim()}>
              Create
            </Button>
          </form>
        }
      />
    );
  }

  const readiness = activeSet?.readiness;

  return (
    <div className="max-w-6xl">
      {/* ── Header: set picker + downloads ── */}
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <Select
            size="md"
            options={sets.map((s) => ({ value: String(s.id), label: s.name }))}
            value={String(activeSet?.id ?? '')}
            onChange={(e) => setSelectedSetId(Number(e.target.value))}
            className="w-auto font-semibold text-green-900"
          />
          {creating ? (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (newSetName.trim()) createSet.mutate(newSetName.trim());
              }}
            >
              <Input
                autoFocus
                size="md"
                value={newSetName}
                onChange={(e) => setNewSetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setCreating(false)}
                placeholder="New set name"
              />
              <Button type="submit" size="md" loading={createSet.isPending} disabled={!newSetName.trim()}>Add</Button>
            </form>
          ) : (
            <Button variant="ghost" size="md" icon={Plus} onClick={() => setCreating(true)}>
              New set
            </Button>
          )}
        </div>

        {activeSet && activeSet.exhibits.length > 0 && (
          <div className="flex items-center gap-2">
            {activeSet.notice_needed && (
              <Button variant="secondary" size="sm" icon={ScrollText} onClick={() => download('notice')} loading={downloading === 'notice'}>
                193.7 Notice
              </Button>
            )}
            <Button variant="secondary" size="sm" icon={FileText} onClick={() => download('list')} loading={downloading === 'list'}>
              Exhibit List
            </Button>
            <Button size="sm" icon={FolderDown} onClick={() => download('zip')} loading={downloading === 'zip'}>
              Trial Pack
            </Button>
          </div>
        )}
      </div>

      {/* ── Readiness strip ── */}
      {activeSet && readiness && readiness.total > 0 && (
        <Card padding="none" className="mb-5 px-4 py-3 flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm font-medium text-green-900">
                {readiness.ready} of {readiness.total} exhibits have an admission path
              </span>
              {readiness.unresolved > 0 && (
                <span className="text-xs text-danger">
                  {readiness.unresolved} need a producer set on Discovery → Documents
                </span>
              )}
            </div>
            <div className="h-2 rounded-full bg-cream-dark overflow-hidden">
              <div
                className={`h-full rounded-full ${readiness.unresolved === 0 ? 'bg-good' : 'bg-warn'}`}
                style={{ width: `${(readiness.ready / readiness.total) * 100}%` }}
              />
            </div>
          </div>
          {readiness.unresolved === 0 && <Check size={20} className="text-good shrink-0" />}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,5fr)_minmax(0,7fr)] gap-6 items-start">
        {/* ── Left: the document shelf ── */}
        <Card padding={false} className="overflow-hidden">
          <div className="px-4 pt-4 pb-3 border-b border-border-subtle">
            <h3 className="t-eyebrow mb-2">Case documents</h3>
            <Input
              size="md"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search accounts, files, periods..."
              leading={<Search size={16} />}
            />
          </div>
          <div className="max-h-[560px] overflow-y-auto divide-y divide-border-subtle">
            {filteredDocs.length === 0 && (
              <p className="px-4 py-8 text-sm text-ink-soft text-center">
                {docsQuery.data?.length ? 'Nothing matches that search.' : 'No documents uploaded yet.'}
              </p>
            )}
            {filteredDocs.map((doc) => {
              const added = inSetDocIds.get(doc.id);
              return (
                <div key={doc.id} className="px-4 py-2 flex items-center gap-3 hover:bg-cream-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink truncate" title={doc.filename}>
                      {doc.filename}
                    </div>
                    <div className="text-xs text-ink-soft truncate flex items-center gap-1">
                      <span className="truncate">{docSubtitle(doc)}</span>
                      <PartyBadge party={doc.source_party} clientName={clientName} opposingPartyName={opposingPartyName} size="xs" />
                    </div>
                  </div>
                  {added ? (
                    <Tag tone="good" icon={Check}>{added}</Tag>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={Plus}
                      onClick={() => activeSet && addDocs.mutate([doc.id])}
                      disabled={!activeSet || addDocs.isPending}
                      title="Add as the next exhibit"
                    >
                      Add
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* ── Right: the exhibits ── */}
        <div className="space-y-2">
          {activeSet && activeSet.exhibits.length === 0 && (
            <Card padding="none">
              <EmptyState
                icon={Gavel}
                title="No exhibits yet"
                description="Add documents from the left — numbering and the admission path are automatic."
              />
            </Card>
          )}
          {activeSet?.exhibits.map((ex, index) => (
            <ExhibitCard
              key={ex.id}
              exhibit={ex}
              isFirst={index === 0}
              isLast={index === activeSet.exhibits.length - 1}
              onMoveUp={() => move(index, -1)}
              onMoveDown={() => move(index, 1)}
              onRemove={() => removeExhibit.mutate(ex.id)}
              onSaveTitle={(title) => patchExhibit.mutate({ id: ex.id, title })}
              onSaveWitness={(w) => patchExhibit.mutate({ id: ex.id, sponsoring_witness: w })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ExhibitCard({ exhibit, isFirst, isLast, onMoveUp, onMoveDown, onRemove, onSaveTitle, onSaveWitness }: {
  exhibit: ExhibitRow;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onSaveTitle: (title: string) => void;
  onSaveWitness: (witness: string) => void;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(exhibit.title);
  const [witnessDraft, setWitnessDraft] = useState(exhibit.sponsoring_witness ?? '');
  const adm = exhibit.admissibility;

  const commitTitle = () => {
    setEditingTitle(false);
    if (titleDraft.trim() && titleDraft.trim() !== exhibit.title) onSaveTitle(titleDraft.trim());
    else setTitleDraft(exhibit.title);
  };

  return (
    <Card padding="compact">
      <div className="flex items-start gap-3">
        <span className="shrink-0 inline-flex items-center justify-center min-w-11 h-7 px-2 rounded-md bg-green-900 text-white text-xs font-semibold tracking-wide">
          {exhibit.number_label}
        </span>

        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <Input
              autoFocus
              size="sm"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTitle();
                if (e.key === 'Escape') { setTitleDraft(exhibit.title); setEditingTitle(false); }
              }}
              className="font-medium"
            />
          ) : (
            // eslint-disable-next-line no-restricted-syntax -- click-to-edit title row: no ui primitive fits this layout
            <button
              onClick={() => setEditingTitle(true)}
              className="group flex items-center gap-1 text-left w-full"
              title="Edit the description that appears on the exhibit list"
            >
              <span className="text-sm font-medium text-ink truncate">{exhibit.title}</span>
              <Pencil size={12} className="text-ink-faint opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
            </button>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Tag
              tone={ADMISSIBILITY_TONE[adm.path]}
              title={`${adm.explanation}\n\n${adm.action}`}
              className="cursor-help"
            >
              {adm.label}{adm.rule ? ` · ${adm.rule}` : ''}
            </Tag>
            {adm.path === 'sponsoring_witness' && (
              <Input
                size="sm"
                value={witnessDraft}
                onChange={(e) => setWitnessDraft(e.target.value)}
                onBlur={() => {
                  if (witnessDraft.trim() !== (exhibit.sponsoring_witness ?? '')) onSaveWitness(witnessDraft.trim());
                }}
                placeholder="Sponsoring witness"
                className="w-40 px-2 text-2xs"
              />
            )}
            {!exhibit.document.has_original && (
              <span className="text-2xs text-danger">Original unavailable — re-upload to include it in the ZIP</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <IconButton
            icon={ArrowUp}
            size="xs"
            variant="ghost"
            aria-label="Move up"
            className="text-ink-faint"
            onClick={onMoveUp}
            disabled={isFirst}
          />
          <IconButton
            icon={ArrowDown}
            size="xs"
            variant="ghost"
            aria-label="Move down"
            className="text-ink-faint"
            onClick={onMoveDown}
            disabled={isLast}
          />
          <IconButton
            icon={X}
            size="xs"
            variant="ghost"
            aria-label="Remove from this set"
            title="Remove from this set (the document itself is untouched)"
            className="text-ink-faint hover:text-danger hover:bg-danger-soft"
            onClick={onRemove}
          />
        </div>
      </div>
    </Card>
  );
}
